/**
 * Contract value ledger.
 *
 * The "true total value" of a project is computed, never stored ad-hoc:
 *
 *   contractValue(deal) = signedProposalBase + sum(approved change order deltas)
 *
 * `signedProposalBase` comes from the immutable signed proposal
 * (`getSignedCommercialListTotal`). Approved change orders live in their own
 * `changeOrders` collection and are never written back into the proposal.
 *
 * `recomputeAndSyncContractValue` is the ONLY path that should write
 * `enhancedDeals.value` for a signed deal — it keeps the deal value (and the
 * cascaded `timberProjects.value`) aligned with the ledger.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  ChangeOrder,
  ChangeOrderLineItem,
  ProjectPLBucket,
  ProposalBuilderData,
} from '../types';
import { getSignedCommercialListTotal } from './proposalPublicGrandTotals';
import { calculateTotals } from './proposalCalculations';
import { computeBondingAdjustment } from './proposalBonding';
import { contractBaseForDeal, priorContractValueForChangeOrder } from './changeOrderSnapshot';

export {
  changeOrdersApprovedBefore,
  compareChangeOrdersByApproval,
  contractBaseForDeal,
  priorContractValueForChangeOrder,
  sumChangeOrdersApprovedBefore,
} from './changeOrderSnapshot';

/** Statuses whose delta counts toward the live contract value. */
export const APPROVED_CHANGE_ORDER_STATUSES: ReadonlyArray<ChangeOrder['status']> = ['approved'];

export function isApprovedChangeOrder(co: Pick<ChangeOrder, 'status'>): boolean {
  return APPROVED_CHANGE_ORDER_STATUSES.includes(co.status);
}

/** Round to whole dollars, preserving sign (change orders may be negative). */
function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function sumLineItems(lineItems: ChangeOrderLineItem[] | undefined): number {
  if (!lineItems || lineItems.length === 0) return 0;
  return lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);
}

/**
 * Derive the billable delta a change order applies to the contract, from its
 * pricing inputs. Used by the editor on save; the result is persisted as
 * `deltaAmount` and is the canonical value summed by `computeContractValue`.
 */
type ChangeOrderDeltaInput = Pick<
  ChangeOrder,
  'pricingMode' | 'lumpSumAmount' | 'lineItems' | 'builderNodeSets' | 'builderProfitMargin' | 'deltaAmount'
> & Partial<Pick<ChangeOrder, 'bondingPercent'>>;

/** Scope delta before any bonding pass-through. */
export function computeChangeOrderBaseDelta(co: ChangeOrderDeltaInput): number {
  switch (co.pricingMode) {
    case 'lump_sum':
      return roundMoney(Number(co.lumpSumAmount) || 0);
    case 'line_item':
      return roundMoney(sumLineItems(co.lineItems));
    case 'builder': {
      if (co.builderNodeSets && co.builderNodeSets.length > 0) {
        const totals = calculateTotals(co.builderNodeSets, co.builderProfitMargin ?? 0);
        return roundMoney(totals.billableTotal);
      }
      // Legacy builder COs stored the full delta; strip any bonding that was folded in.
      return roundMoney((Number(co.deltaAmount) || 0) - (Number((co as Partial<ChangeOrder>).bondingAdjustment) || 0));
    }
    default:
      return roundMoney((Number(co.deltaAmount) || 0) - (Number((co as Partial<ChangeOrder>).bondingAdjustment) || 0));
  }
}

/**
 * Base scope delta and the bonding premium change that rides on it (the
 * proposal's bonding percent, captured on the CO). COs without
 * `bondingPercent` — every CO written before bonding existed — get 0.
 */
export function splitChangeOrderDelta(co: ChangeOrderDeltaInput): { base: number; bonding: number; total: number } {
  const base = computeChangeOrderBaseDelta(co);
  const bonding = computeBondingAdjustment(base, Number(co.bondingPercent) || 0);
  return { base, bonding, total: base + bonding };
}

export function computeChangeOrderDelta(co: ChangeOrderDeltaInput): number {
  return splitChangeOrderDelta(co).total;
}

/**
 * Per-bucket RAW cost contribution of a change order for Project P&L budgeting.
 * Prefers an explicit `budgetCostByBucket`; otherwise derives from line items or
 * builder node sets. Lump sums contribute nothing to the budget unless an
 * explicit breakdown is supplied.
 */
export function computeChangeOrderBudgetByBucket(
  co: Pick<
    ChangeOrder,
    'pricingMode' | 'lineItems' | 'builderNodeSets' | 'budgetCostByBucket'
  >,
): Partial<Record<ProjectPLBucket, number>> {
  if (co.budgetCostByBucket && Object.keys(co.budgetCostByBucket).length > 0) {
    return co.budgetCostByBucket;
  }

  const out: Partial<Record<ProjectPLBucket, number>> = {};
  const add = (bucket: ProjectPLBucket, amount: number) => {
    if (!amount) return;
    out[bucket] = (out[bucket] || 0) + amount;
  };

  if (co.pricingMode === 'line_item' && co.lineItems) {
    co.lineItems.forEach(li => {
      const cost = typeof li.costEstimate === 'number' ? li.costEstimate : Number(li.amount) || 0;
      add(li.bucket || 'unassigned', cost);
    });
    return out;
  }

  if (co.pricingMode === 'builder' && co.builderNodeSets) {
    co.builderNodeSets
      .filter(ns => ns.enabled !== false)
      .forEach(ns => {
        const qty = ns.quantity || 1;
        add('design', (ns.design?.total || 0) * qty);
        add('timber', (ns.materials?.total || 0) * qty);
        add('production', (ns.production?.total || 0) * qty);
        add('freight', (ns.freight?.total || 0) * qty);
        add(
          'install',
          ((ns.install?.total || 0) + (ns.overhead?.total || 0) + (ns.install?.overheadTotal || 0)) * qty,
        );
        add('travel', (ns.travel?.total || 0) * qty);
      });
  }

  return out;
}

/**
 * Sum of the deltas of approved change orders. Pure — operates on whatever set
 * of change orders is passed in.
 */
export function sumApprovedChangeOrders(changeOrders: ChangeOrder[] | undefined | null): number {
  if (!changeOrders || changeOrders.length === 0) return 0;
  return changeOrders.reduce((s, co) => (isApprovedChangeOrder(co) ? s + (Number(co.deltaAmount) || 0) : s), 0);
}

/**
 * The contract value: signed proposal base + approved change order deltas.
 * Returns `null` when there is no signed base to build on (so callers can avoid
 * clobbering an unsigned deal value).
 */
export function computeContractValue(
  signedBase: number | null,
  changeOrders: ChangeOrder[] | undefined | null,
): number | null {
  if (signedBase == null) return null;
  return roundMoney(signedBase + sumApprovedChangeOrders(changeOrders));
}

export interface RecomputeContractValueDeps {
  /** Preferred writer — CRMContext.updateEnhancedDeal also cascades to timberProjects.value. */
  updateEnhancedDeal?: (id: string, updates: { value: number }) => Promise<void>;
}

export interface RecomputeContractValueResult {
  applied: boolean;
  signedBase: number | null;
  changeOrderTotal: number;
  contractValue: number | null;
}

/**
 * Read the signed proposal base + approved change orders for a deal and write
 * the resulting contract value to `enhancedDeals.value`. Idempotent: safe to
 * call from the client sign flow and from a Cloud Function safety net.
 *
 * No-ops when the proposal is not signed (so it can never disturb an in-flight,
 * unsigned proposal/deal value).
 */
export async function recomputeAndSyncContractValue(
  dealId: string,
  deps?: RecomputeContractValueDeps,
): Promise<RecomputeContractValueResult> {
  const noResult: RecomputeContractValueResult = {
    applied: false,
    signedBase: null,
    changeOrderTotal: 0,
    contractValue: null,
  };
  if (!dealId) return noResult;

  // Proposal doc id == dealId (canonical). Read the signed base.
  let signedBase: number | null = null;
  try {
    const propSnap = await getDoc(doc(db, 'proposalBuilders', dealId));
    if (propSnap.exists()) {
      signedBase = getSignedCommercialListTotal(propSnap.data() as ProposalBuilderData);
    }
  } catch (err) {
    console.warn('[contractValue] failed to read proposal for', dealId, err);
  }

  // Unsigned proposal -> never touch deal value.
  if (signedBase == null) return noResult;

  // Approved change orders for this deal.
  let changeOrders: ChangeOrder[] = [];
  try {
    const coSnap = await getDocs(query(collection(db, 'changeOrders'), where('dealId', '==', dealId)));
    changeOrders = coSnap.docs.map(d => ({ id: d.id, ...(d.data() as ChangeOrder) }));
  } catch (err) {
    console.warn('[contractValue] failed to read change orders for', dealId, err);
  }

  const changeOrderTotal = sumApprovedChangeOrders(changeOrders);
  const contractValue = roundMoney(signedBase + changeOrderTotal);

  try {
    if (deps?.updateEnhancedDeal) {
      await deps.updateEnhancedDeal(dealId, { value: contractValue });
    } else {
      // Fallback: replicate CRMContext.updateEnhancedDeal value cascade so this
      // works from contexts without CRM access (e.g. portal page).
      await updateDoc(doc(db, 'enhancedDeals', dealId), {
        value: contractValue,
        updatedAt: Timestamp.fromDate(new Date()),
      });
      try {
        const tps = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
        const nowTs = Timestamp.fromDate(new Date());
        await Promise.all(tps.docs.map(d => updateDoc(d.ref, { value: contractValue, updatedAt: nowTs })));
      } catch (syncErr) {
        console.warn('[contractValue] could not cascade to timber project value for', dealId, syncErr);
      }
    }
  } catch (err) {
    console.error('[contractValue] failed to write contract value for', dealId, err);
    return { applied: false, signedBase, changeOrderTotal, contractValue };
  }

  return { applied: true, signedBase, changeOrderTotal, contractValue };
}

export interface ChangeOrderLedgerContext {
  /** Proposal builder doc (signed or not), when one exists. */
  proposal: ProposalBuilderData | null;
  /** Signed proposal commercial total, or null when the proposal is not signed. */
  signedBase: number | null;
  /** `enhancedDeals.value` — the legacy base for deals with no CRM proposal. */
  dealValue: number;
  /** Every change order on the deal (all statuses). */
  changeOrders: ChangeOrder[];
  /** Starting point the CO ledger layers onto (see `contractBaseForDeal`). */
  base: number;
}

/** Signed base, deal value, and all COs for a deal — one read for every CO surface. */
export async function loadChangeOrderLedgerContext(dealId: string): Promise<ChangeOrderLedgerContext> {
  let proposal: ProposalBuilderData | null = null;
  let signedBase: number | null = null;
  let dealValue = 0;
  let changeOrders: ChangeOrder[] = [];
  const [propRes, dealRes, coRes] = await Promise.allSettled([
    getDoc(doc(db, 'proposalBuilders', dealId)),
    getDoc(doc(db, 'enhancedDeals', dealId)),
    getDocs(query(collection(db, 'changeOrders'), where('dealId', '==', dealId))),
  ]);
  if (propRes.status === 'fulfilled' && propRes.value.exists()) {
    proposal = propRes.value.data() as ProposalBuilderData;
    signedBase = getSignedCommercialListTotal(proposal);
  }
  if (dealRes.status === 'fulfilled' && dealRes.value.exists()) {
    dealValue = Number((dealRes.value.data() as { value?: unknown }).value) || 0;
  }
  if (coRes.status === 'fulfilled') {
    changeOrders = coRes.value.docs.map(d => ({ id: d.id, ...(d.data() as ChangeOrder) }));
  }
  return {
    proposal,
    signedBase,
    dealValue,
    changeOrders,
    base: contractBaseForDeal(signedBase, dealValue, changeOrders),
  };
}

/**
 * Contract value immediately before this change order applied.
 * Uses the stamped snapshot when present; otherwise base + COs approved
 * before this one (never COs approved later).
 */
export async function computePriorContractValueBeforeChangeOrder(
  co: Pick<ChangeOrder, 'id' | 'dealId' | 'status' | 'approvedAt' | 'number' | 'contractValueBefore'>,
): Promise<number> {
  if (typeof co.contractValueBefore === 'number' && Number.isFinite(co.contractValueBefore)) {
    return co.contractValueBefore;
  }
  const ctx = await loadChangeOrderLedgerContext(co.dealId);
  // Prefer the stored copy of this CO (fresh approvedAt) when the caller passed a stale one.
  const stored = ctx.changeOrders.find(c => c.id === co.id);
  return priorContractValueForChangeOrder(stored ?? co, ctx.base, ctx.changeOrders);
}
